import { Box, Flex, HStack, Input, Text, useColorMode } from '@chakra-ui/react';
import {
  NotificationType,
  TransactionStageType,
} from '@client/graphql/__generated__/types';
import { Select } from '@document/common/Select';
import dayjs from 'dayjs';
import { FC, useMemo } from 'react';
import DatePicker from 'react-datepicker';
import { useSearchParams } from 'react-router-dom';

interface FilterOption {
  label: string;
  value: string;
}

const darkSelectStyles = {
  option: (
    provided: Record<string, unknown>,
    state: { isSelected: boolean; isFocused: boolean }
  ) => ({
    ...provided,
    bg: state.isSelected
      ? 'whiteAlpha.200'
      : state.isFocused
        ? 'whiteAlpha.100'
        : 'transparent',
    color: 'white',
    fontSize: 'sm',
    _hover: { bg: 'whiteAlpha.100' },
  }),
};

interface FilterSelectProps {
  label: string;
  value: string;
  options: FilterOption[];
  onChange: (value: string) => void;
}

const FilterSelect: FC<FilterSelectProps> = ({
  label,
  value,
  options,
  onChange,
}) => {
  const selectedOption = options.find((opt) => opt.value === value) ?? null;

  return (
    <Select<FilterOption>
      chakraStyles={darkSelectStyles}
      components={{
        SingleValue: (props) => (
          <Text color="white" fontSize="sm" gridArea="1/1/2/3" mr={2}>
            <Text as="span" color="whiteAlpha.600" mr={2}>
              {label}
            </Text>{' '}
            {props.data.label}
          </Text>
        ),
      }}
      getOptionLabel={(opt) => opt.label}
      getOptionValue={(opt) => opt.value}
      isSearchable={false}
      options={options}
      size="sm"
      value={selectedOption}
      onChange={(newValue) => {
        if (newValue) {
          onChange(newValue.value);
        }
      }}
    />
  );
};

const eventTypeLabels: Record<TransactionStageType, string> = {
  [TransactionStageType.LEAD]: 'Lead',
  [TransactionStageType.SIGNED_AGENCY_AGREEMENT]: 'Agency Agreement',
  [TransactionStageType.ACTIVE]: 'Active',
  [TransactionStageType.SUBMITTED_OFFER]: 'Submitted Offer',
  [TransactionStageType.UNDER_CONTRACT]: 'Under Contract',
  [TransactionStageType.CLOSED]: 'Closing',
  [TransactionStageType.TERMINATED]: 'Terminated',
};

const notificationTypeLabels: Record<NotificationType, string> = {
  [NotificationType.TEXT]: 'Text Message',
  [NotificationType.EMAIL]: 'Email',
};

export const NotificationsFilters: FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { colorMode } = useColorMode();

  const title = searchParams.get('title') ?? '';
  const recipientId = searchParams.get('recipientId') ?? '';
  const eventType = searchParams.get('eventType') ?? '';
  const notificationType = searchParams.get('notificationType') ?? '';
  const dateFrom = searchParams.get('dateFrom');
  const dateTo = searchParams.get('dateTo');

  const updateFilter = (key: string, value: string) => {
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set(key, value);
    } else {
      newParams.delete(key);
    }
    setSearchParams(newParams);
  };

  const eventTypeOptions: FilterOption[] = useMemo(
    () => [
      { label: 'All', value: '' },
      ...Object.entries(eventTypeLabels).map(([value, label]) => ({
        label,
        value,
      })),
    ],
    []
  );

  const notificationTypeOptions: FilterOption[] = useMemo(
    () => [
      { label: 'All', value: '' },
      ...Object.entries(notificationTypeLabels).map(([value, label]) => ({
        label,
        value,
      })),
    ],
    []
  );

  // Title filter is a placeholder - will be functional when backend supports it
  const titleOptions: FilterOption[] = [{ label: 'All', value: '' }];

  // Recipient filter is a placeholder - could be populated from a separate query
  const recipientOptions: FilterOption[] = [{ label: 'All', value: '' }];

  const parsedDateFrom = dateFrom ? dayjs(dateFrom).toDate() : null;
  const parsedDateTo = dateTo ? dayjs(dateTo).toDate() : null;

  const handleDateFromChange = (date: Date | null) => {
    updateFilter('dateFrom', date ? dayjs(date).format('YYYY-MM-DD') : '');
  };

  const handleDateToChange = (date: Date | null) => {
    updateFilter('dateTo', date ? dayjs(date).format('YYYY-MM-DD') : '');
  };

  return (
    <HStack
      flexWrap="wrap"
      gap={3}
      pl={{ base: 0, md: 6, lg: 10 }}
      py={4}
      spacing={0}
    >
      <FilterSelect
        label="Title"
        options={titleOptions}
        value={title}
        onChange={(value) => updateFilter('title', value)}
      />
      <FilterSelect
        label="Recipient"
        options={recipientOptions}
        value={recipientId}
        onChange={(value) => updateFilter('recipientId', value)}
      />
      <FilterSelect
        label="Event"
        options={eventTypeOptions}
        value={eventType}
        onChange={(value) => updateFilter('eventType', value)}
      />
      <FilterSelect
        label="Type"
        options={notificationTypeOptions}
        value={notificationType}
        onChange={(value) => updateFilter('notificationType', value)}
      />
      <Box
        bg="whiteAlpha.50"
        border="1px solid"
        borderColor="whiteAlpha.100"
        borderRadius="sm"
        px={3}
        py={0}
      >
        <Flex alignItems="center" gap={2}>
          <Text color="whiteAlpha.600" fontSize="sm">
            Date sent
          </Text>
          <Flex alignItems="center">
            <DatePicker
              customInput={
                <Input
                  bg="transparent"
                  borderColor="transparent"
                  color="white"
                  fontSize="sm"
                  placeholder="From"
                  size="sm"
                  width="90px"
                />
              }
              maxDate={parsedDateTo ?? undefined}
              placeholderText="From"
              popperClassName={colorMode === 'dark' ? 'dark' : undefined}
              selected={parsedDateFrom}
              onChange={handleDateFromChange}
            />
            <Text color="whiteAlpha.400" mx={2}>
              -
            </Text>
            <DatePicker
              customInput={
                <Input
                  bg="transparent"
                  borderColor="transparent"
                  color="white"
                  fontSize="sm"
                  placeholder="To"
                  size="sm"
                  width="90px"
                />
              }
              minDate={parsedDateFrom ?? undefined}
              placeholderText="To"
              popperClassName={colorMode === 'dark' ? 'dark' : undefined}
              selected={parsedDateTo}
              onChange={handleDateToChange}
            />
          </Flex>
        </Flex>
      </Box>
    </HStack>
  );
};
